<template>
  <v-container
    fluid
    class="change-leverage-page responsive-height"
    :class="{ 'change-leverage-page--dark': $vuetify.theme.dark }"
  >
    <section class="leverage-panel accounts-panel">
      <header class="panel-heading">
        <v-icon size="21">mdi-scale-balance</v-icon>
        <h2>Leverage</h2>
      </header>

      <div class="accounts-table-wrap">
        <table class="accounts-table">
          <thead>
            <tr>
              <th>SL.</th>
              <th>MT5 Account No.</th>
              <th>Leverage</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="fetching">
              <td colspan="3" class="table-message">Loading accounts...</td>
            </tr>
            <tr v-else-if="!pagedAccounts.length">
              <td colspan="3" class="table-message">No MT5 accounts found.</td>
            </tr>
            <template v-else>
              <tr v-for="(account, index) in pagedAccounts" :key="accountKey(account, index)">
                <td>{{ accountStart + index + 1 }}</td>
                <td>{{ accountLabel(account) }}</td>
                <td>{{ leverageLabel(account.leverage) }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <footer class="accounts-footer">
        <label class="rows-select">
          <span>Rows per page:</span>
          <select v-model.number="rowsPerPage" aria-label="Rows per page">
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="25">25</option>
          </select>
          <v-icon size="18">mdi-chevron-down</v-icon>
        </label>
        <span class="accounts-range">{{ accountRange }}</span>
        <button type="button" aria-label="Previous page" :disabled="accountPage <= 1" @click="accountPage -= 1">
          <v-icon size="22">mdi-chevron-left</v-icon>
        </button>
        <button type="button" aria-label="Next page" :disabled="accountPage >= accountPages" @click="accountPage += 1">
          <v-icon size="22">mdi-chevron-right</v-icon>
        </button>
      </footer>
    </section>

    <section class="change-form-panel">
      <div class="change-form-grid">
        <div class="leverage-panel form-card">
          <header class="panel-heading form-heading">
            <v-icon size="21">mdi-tune-variant</v-icon>
            <h2>Change Your Trading Account Leverage From Here</h2>
          </header>

          <form class="leverage-form" @submit.prevent="saveLeverage">
          <label class="form-field">
            <span>MT5 Account</span>
            <div class="select-control">
              <select v-model="selectedAccount" :disabled="!isEditable" aria-label="MT5 Account">
                <option disabled value="">Select MT5 Account</option>
                <option
                  v-for="account in userleverages"
                  :key="accountKey(account)"
                  :value="account.trading_account_no"
                >
                  {{ accountLabel(account) }}
                </option>
              </select>
              <v-icon size="20">mdi-chevron-down</v-icon>
            </div>
          </label>

          <label class="form-field">
            <span>Current Leverage</span>
            <input :value="currentLeverage" readonly placeholder="1:50" />
          </label>

          <div ref="leverageDropdown" class="form-field leverage-dropdown-field">
            <span>New Leverage</span>
            <button
              type="button"
              class="leverage-dropdown-trigger"
              :class="{ 'leverage-dropdown-trigger--open': dropdownOpen }"
              aria-haspopup="listbox"
              :aria-expanded="dropdownOpen ? 'true' : 'false'"
              @click="dropdownOpen = !dropdownOpen"
            >
              <span :class="{ 'placeholder-text': !newLeverage }">
                {{ newLeverage || 'Select New Leverage' }}
              </span>
              <v-icon size="20">mdi-chevron-down</v-icon>
            </button>
            <ul v-show="dropdownOpen" class="leverage-options" role="listbox">
              <li v-if="!sortedLeverages.length" class="leverage-option leverage-option--empty">
                No leverage options available
              </li>
              <template v-else>
                <li
                  v-for="option in sortedLeverages"
                  :key="option"
                  class="leverage-option"
                  :class="{ 'leverage-option--highlighted': option === highlightedLeverage }"
                  role="option"
                  :aria-selected="newLeverage === option ? 'true' : 'false'"
                  @mouseenter="hoveredLeverage = option"
                  @mouseleave="hoveredLeverage = ''"
                  @click="selectLeverage(option)"
                >
                  {{ option }}
                </li>
              </template>
            </ul>
          </div>

          <button type="submit" class="save-button" :disabled="loading">
            <v-progress-circular v-if="loading" indeterminate size="17" width="2" />
            <v-icon v-else size="19">mdi-content-save-outline</v-icon>
            <span>{{ loading ? 'Saving...' : 'Save Change' }}</span>
          </button>
          </form>
        </div>

        <aside class="leverage-information">
          <article class="information-card">
            <div class="information-title">
              <v-icon size="29">mdi-shield-check-outline</v-icon>
              <h3>Important Information</h3>
            </div>
            <ul>
              <li v-for="item in importantInformation" :key="item">
                <v-icon size="20">mdi-check-circle-outline</v-icon>
                <span>{{ item }}</span>
              </li>
            </ul>
          </article>

          <article class="note-card">
            <v-icon size="29">mdi-information-outline</v-icon>
            <p><strong>Note:</strong> You can change leverage only when you have no open positions.</p>
          </article>
        </aside>
      </div>
    </section>
  </v-container>
</template>

<script>
import { LEVERAGE__ACTIONS } from "@/store/action-types";
import toast from "@/services/Notification";
import { mapActions, mapState } from "vuex";

export default {
  name: "ChangeLeverage",
  data() {
    return {
      selectedAccount: "",
      currentLeverage: "",
      newLeverage: "",
      hoveredLeverage: "",
      isEditable: true,
      sortedLeverages: [],
      loading: false,
      fetching: true,
      dropdownOpen: false,
      rowsPerPage: 10,
      accountPage: 1,
      importantInformation: [
        "Changing leverage will affect your trading position margin requirements.",
        "Higher leverage increases both potential profit and risk.",
        "Make sure to have sufficient balance before changing leverage.",
        "This change will apply immediately after confirmation.",
      ],
    };
  },
  computed: {
    ...mapState("leverage", ["userleverages", "allleverages"]),
    accountPages() {
      return Math.max(1, Math.ceil(this.userleverages.length / this.rowsPerPage));
    },
    accountStart() {
      return (this.accountPage - 1) * this.rowsPerPage;
    },
    pagedAccounts() {
      return this.userleverages.slice(this.accountStart, this.accountStart + this.rowsPerPage);
    },
    accountRange() {
      if (!this.userleverages.length) return "0–0 of 0";
      const end = Math.min(this.accountStart + this.rowsPerPage, this.userleverages.length);
      return `${this.accountStart + 1}–${end} of ${this.userleverages.length}`;
    },
    highlightedLeverage() {
      return this.hoveredLeverage || this.newLeverage || this.currentLeverage;
    },
  },
  watch: {
    selectedAccount(newAccount) {
      const account = this.userleverages.find(
        (item) => String(item.trading_account_no) === String(newAccount)
      );
      this.currentLeverage = account ? this.leverageLabel(account.leverage) : "";
      this.newLeverage = "";
    },
    allleverages: {
      immediate: true,
      handler(newAllLeverages) {
        this.updateSortedLeverages(newAllLeverages || []);
      },
    },
    rowsPerPage() {
      this.accountPage = 1;
    },
    accountPages(value) {
      if (this.accountPage > value) this.accountPage = value;
    },
  },
  methods: {
    ...mapActions("leverage", [
      LEVERAGE__ACTIONS.ALLLEVERAGES,
      LEVERAGE__ACTIONS.USERLEVERAGE,
      LEVERAGE__ACTIONS.UPDATELEVERAGE,
    ]),
    accountKey(account, index = 0) {
      return account.id || account.trading_account_no || index;
    },
    accountType(account) {
      return account.tradingaccounttypes?.account_type_display ||
        account.account_type_display ||
        account.account_type ||
        "";
    },
    accountLabel(account) {
      const type = this.accountType(account);
      return `${account.trading_account_no}${type ? ` [${String(type).toUpperCase()}]` : ""}`;
    },
    leverageLabel(value) {
      if (value === undefined || value === null || value === "") return "";
      return String(value).startsWith("1:") ? String(value) : `1:${value}`;
    },
    selectLeverage(option) {
      this.newLeverage = option;
      this.dropdownOpen = false;
    },
    closeDropdownOnOutsideClick(event) {
      const dropdown = this.$refs.leverageDropdown;
      if (this.dropdownOpen && dropdown && !dropdown.contains(event.target)) {
        this.dropdownOpen = false;
      }
    },
    async saveLeverage() {
      if (!this.selectedAccount) {
        toast.error("Please select an MT5 account");
        return;
      }
      if (!this.newLeverage) {
        toast.error("Please select a new leverage");
        return;
      }
      if (this.newLeverage === this.currentLeverage) {
        toast.error("Please select a leverage different from the current leverage");
        return;
      }

      this.loading = true;
      try {
        const updated = await this[LEVERAGE__ACTIONS.UPDATELEVERAGE]({
          trading_account_no: this.selectedAccount,
          new_leverage: Number(this.newLeverage.split(":")[1]),
          currentLeverage: Number(this.currentLeverage.split(":")[1]),
        });
        if (updated) {
          this.newLeverage = "";
          this.dropdownOpen = false;
        }
      } finally {
        this.loading = false;
      }
    },
    updateSortedLeverages(leverages) {
      this.sortedLeverages = leverages
        .slice()
        .sort((a, b) => Number(a.leverage) - Number(b.leverage))
        .map((item) => this.leverageLabel(item.leverage));
    },
  },
  async mounted() {
    document.addEventListener("click", this.closeDropdownOnOutsideClick);

    const queryAccount = this.$route.query.account;
    try {
      await Promise.all([
        this[LEVERAGE__ACTIONS.ALLLEVERAGES](),
        this[LEVERAGE__ACTIONS.USERLEVERAGE](),
      ]);

      if (queryAccount) {
        this.selectedAccount = queryAccount;
        this.isEditable = false;
      } else if (this.userleverages.length) {
        this.selectedAccount = this.userleverages[0].trading_account_no;
      }
    } finally {
      this.fetching = false;
    }
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeDropdownOnOutsideClick);
  },
};
</script>

<style scoped>
.change-leverage-page {
  --page-bg: #f7f8fa;
  --panel-bg: #ffffff;
  --field-bg: #ffffff;
  --dropdown-bg: #ffffff;
  --table-head: #f4f5f7;
  --border: #e0e3e7;
  --border-soft: #e8eaed;
  --text: #15181d;
  --muted: #5f6670;
  --subtle: #858b94;
  --primary: #d99a00;
  --primary-soft: rgba(217, 154, 0, 0.09);
  height: calc(100vh - 64px);
  min-height: calc(100vh - 64px);
  padding: 26px 24px 30px;
  color: var(--text);
  background: var(--page-bg);
  font-family: "Inter", sans-serif;
}

.change-leverage-page--dark {
  --page-bg: #030b13;
  --panel-bg: #07111b;
  --field-bg: #08131e;
  --dropdown-bg: #0b1621;
  --table-head: #101923;
  --border: #443716;
  --border-soft: #293026;
  --text: #f4f6fb;
  --muted: #afb6bf;
  --subtle: #858f99;
  --primary: #ffc400;
  --primary-soft: rgba(227, 166, 0, 0.13);
}

.leverage-panel {
  background: var(--panel-bg);
  border: 1px solid var(--border);
  border-radius: 7px;
}

.accounts-panel {
  min-height: 219px;
  margin-bottom: 19px;
  padding: 0 24px;
}

.panel-heading {
  display: flex;
  height: 54px;
  align-items: center;
  gap: 11px;
  border-bottom: 1px solid var(--border-soft);
}

.panel-heading .v-icon {
  color: var(--primary);
}

.panel-heading h2 {
  margin: 0;
  color: var(--text);
  font-size: 15px;
  font-weight: 600;
}

.accounts-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.accounts-table th,
.accounts-table td {
  height: 34px;
  padding: 0 46px;
  text-align: left;
  border-bottom: 1px solid var(--border-soft);
  white-space: nowrap;
  font-size: 12px;
}

.accounts-table th {
  color: var(--muted);
  background: var(--table-head);
  font-weight: 400;
}

.accounts-table td {
  color: var(--text);
}

.accounts-table tbody td:first-child,
.accounts-table tbody td:last-child {
  color: var(--primary);
  font-weight: 600;
}

.accounts-table th:nth-child(1),
.accounts-table td:nth-child(1) {
  width: 20%;
}

.accounts-table th:nth-child(2),
.accounts-table td:nth-child(2) {
  width: 55%;
  text-align: center;
}

.accounts-table th:nth-child(3),
.accounts-table td:nth-child(3) {
  width: 25%;
  text-align: center;
}

.table-message {
  text-align: center !important;
  color: var(--muted) !important;
}

.accounts-footer {
  display: flex;
  height: 54px;
  padding-right: 18px;
  align-items: center;
  justify-content: flex-end;
  gap: 38px;
  color: var(--text);
  font-size: 12px;
}

.rows-select {
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
}

.rows-select select {
  width: 52px;
  height: 38px;
  padding: 0 23px 0 6px;
  color: var(--text);
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--border);
  outline: 0;
  appearance: none;
  font-family: inherit;
  font-size: 12px;
}

.rows-select .v-icon {
  position: absolute;
  right: 2px;
  color: var(--primary);
  pointer-events: none;
}

.accounts-footer button {
  display: grid;
  width: 24px;
  height: 34px;
  place-items: center;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.accounts-footer button .v-icon {
  color: var(--subtle);
}

.accounts-footer button:last-child .v-icon {
  color: var(--primary);
}

.accounts-footer button:disabled {
  opacity: 0.45;
  cursor: default;
}

.change-form-panel { min-height: 360px; }

.change-form-grid {
  display: grid;
  grid-template-columns: minmax(520px, 1.1fr) minmax(390px, 1fr);
  gap: 12px;
}

.form-card { padding: 0 22px 22px; }

.form-heading { border-bottom: 0; }

.leverage-form {
  min-width: 0;
}

.form-field {
  display: flex;
  margin-bottom: 18px;
  flex-direction: column;
  gap: 7px;
}

.form-field > span {
  color: var(--text);
  font-size: 12px;
  font-weight: 600;
}

.form-field input,
.select-control,
.leverage-dropdown-trigger {
  width: 100%;
  height: 43px;
  color: var(--text);
  background: var(--field-bg);
  border: 1px solid var(--border);
  border-radius: 6px;
  outline: 0;
  font-family: inherit;
  font-size: 13px;
}

.form-field input {
  padding: 0 14px;
}

.select-control {
  position: relative;
  display: flex;
  align-items: center;
}

.select-control select {
  width: 100%;
  height: 100%;
  padding: 0 42px 0 14px;
  color: var(--text);
  background: transparent;
  border: 0;
  outline: 0;
  appearance: none;
  font-family: inherit;
  font-size: 13px;
}

.select-control select:disabled {
  opacity: 1;
}

.select-control select option {
  color: var(--text);
  background: var(--dropdown-bg);
}

.select-control .v-icon,
.leverage-dropdown-trigger .v-icon {
  position: absolute;
  right: 12px;
  color: var(--muted);
  pointer-events: none;
}

.leverage-dropdown-field {
  position: relative;
  margin-bottom: 0;
}

.leverage-dropdown-trigger {
  position: relative;
  display: flex;
  padding: 0 42px 0 14px;
  align-items: center;
  text-align: left;
  cursor: pointer;
}

.leverage-dropdown-trigger--open {
  border-color: var(--primary);
  box-shadow: 0 0 0 1px var(--primary-soft);
}

.placeholder-text {
  color: var(--subtle);
}

.leverage-options {
  position: absolute;
  z-index: 4;
  top: 69px;
  right: 0;
  left: 0;
  max-height: 325px;
  margin: 0;
  padding: 9px 5px;
  overflow-y: auto;
  list-style: none;
  background: var(--dropdown-bg);
  border: 1px solid var(--border);
  border-top: 0;
  border-radius: 0 0 5px 5px;
  box-shadow: 0 8px 18px rgba(6, 16, 34, 0.1);
}

.leverage-option {
  height: 30px;
  padding: 0 13px;
  color: var(--text);
  border-radius: 4px;
  line-height: 30px;
  font-size: 13px;
  cursor: pointer;
}

.leverage-option--highlighted {
  color: var(--primary);
  background: var(--primary-soft);
}

.change-leverage-page--dark .leverage-option--highlighted {
  color: #ffc400;
  background: linear-gradient(90deg, rgba(92, 68, 0, 0.72), rgba(63, 48, 4, 0.62));
}

.leverage-option--empty {
  color: var(--muted);
  cursor: default;
}

.save-button {
  display: flex;
  height: 40px;
  margin-top: 30px;
  padding: 0 17px;
  align-items: center;
  gap: 9px;
  color: #17130a;
  background: linear-gradient(180deg, #ffc826, #e4a500);
  border: 0;
  border-radius: 4px;
  box-shadow: 0 5px 12px rgba(191, 137, 0, 0.22);
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
}

.save-button .v-icon,
.save-button .v-progress-circular {
  color: #17130a;
}

.save-button:disabled {
  opacity: 0.7;
  cursor: wait;
}

.leverage-information {
  display: flex;
  padding: 0 22px 20px;
  flex-direction: column;
  gap: 16px;
  background: var(--panel-bg);
  border: 1px solid var(--border);
  border-radius: 7px;
}

.information-card,
.note-card {
  border: 1px solid var(--border);
  border-radius: 6px;
}

.information-card {
  min-height: 246px;
  padding: 23px 4px;
  border: 0;
}

.information-title,
.note-card,
.information-card li {
  display: flex;
  align-items: flex-start;
}

.information-title {
  align-items: center;
  gap: 14px;
}

.information-title .v-icon,
.note-card > .v-icon,
.information-card li .v-icon {
  color: var(--primary);
}

.information-title h3 {
  margin: 0;
  color: var(--primary);
  font-size: 16px;
  font-weight: 600;
}

.information-card ul {
  display: flex;
  margin: 22px 0 0;
  padding: 0;
  flex-direction: column;
  gap: 20px;
  list-style: none;
}

.information-card li {
  gap: 16px;
  color: var(--text);
  font-size: 13px;
  line-height: 20px;
}

.information-card li .v-icon {
  margin-top: 1px;
  flex: 0 0 20px;
}

.note-card {
  min-height: 87px;
  padding: 21px 24px;
  gap: 14px;
  color: var(--text);
  background: rgba(217, 154, 0, 0.035);
  border-color: rgba(217, 154, 0, 0.3);
}

.change-leverage-page--dark .note-card {
  background: rgba(92, 68, 0, 0.14);
}

.note-card p {
  margin: 1px 0 0;
  font-size: 13px;
  line-height: 20px;
}

.note-card strong {
  color: var(--primary);
}

@media (max-width: 1250px) {
  .change-form-grid {
    grid-template-columns: minmax(430px, 1.35fr) minmax(340px, 1fr);
    gap: 24px;
  }

  .accounts-table th,
  .accounts-table td {
    padding-right: 24px;
    padding-left: 24px;
  }
}

@media (max-width: 980px) {
  .change-leverage-page {
    padding: 18px;
  }

  .change-form-grid {
    grid-template-columns: 1fr;
  }

  .change-form-panel {
    padding-bottom: 24px;
  }

  .save-button {
    margin-top: 30px;
  }
}

@media (max-width: 650px) {
  .change-leverage-page {
    height: calc(100vh - 56px);
    padding: 12px;
  }

  .accounts-panel,
  .change-form-panel {
    padding-right: 14px;
    padding-left: 14px;
  }

  .accounts-table {
    min-width: 620px;
  }

  .accounts-table-wrap {
    overflow-x: auto;
  }

  .accounts-footer {
    padding-right: 0;
    gap: 14px;
  }

  .rows-select {
    gap: 8px;
  }

  .form-heading {
    height: auto;
    min-height: 60px;
  }

  .form-heading h2 {
    line-height: 20px;
  }

  .information-card {
    padding: 20px;
  }
}
</style>
